// Mobile scheduling-messaging client (native app, Supabase/JWT path).
//
// The mobile surface is isolated from the shared kiosk / web messenger: it reads
// ONLY 'mobile'-surface conversations (list_my_mobile_conversations /
// get_my_mobile_unread_summary, added in
// 20260819170000_messaging_mobile_surface_isolation.sql) and creates threads via
// the scoped create RPC (create_mobile_scheduling_conversation,
// 20260819180000). Everything downstream of a conversation id — loading a page of
// messages, sending, marking read, reactions, attachments — is surface-agnostic
// and reuses messaging-client.ts (a conversation id is unique across surfaces and
// RLS gates it to members), so we do NOT duplicate those here.
//
// See docs/messaging/mobile-scheduling-messaging-design.md.

import { supabase } from '@/integrations/supabase/client';
import type {
  ConversationListEntry,
  UnreadSummaryRow,
} from '@/types/messaging';
import type { MobileSchedulingReferenceType } from '@/lib/messaging/mobile-scheduling-scope';

type Result<T> = { data: T; error: null } | { data: null; error: { message: string; code?: string } };

const ok = <T>(data: T): Result<T> => ({ data, error: null });
const fail = (error: { message?: string; code?: string } | null | undefined): Result<never> => ({
  data: null,
  error: { message: error?.message ?? 'Unknown error', code: error?.code },
});

export async function listMobileConversations(): Promise<Result<ConversationListEntry[]>> {
  const { data, error } = await supabase.rpc('list_my_mobile_conversations');
  if (error) return fail(error);
  return ok((data ?? []) as ConversationListEntry[]);
}

export async function getMobileUnreadSummary(): Promise<Result<UnreadSummaryRow[]>> {
  const { data, error } = await supabase.rpc('get_my_mobile_unread_summary');
  if (error) return fail(error);
  return ok((data ?? []) as UnreadSummaryRow[]);
}

export interface CreateMobileSchedulingConversationInput {
  referenceType: MobileSchedulingReferenceType;
  referenceId: string;
  /** Counterparties to include; the RPC validates each with can_message_profile. */
  memberIds: string[];
  name?: string | null;
}

export async function createMobileSchedulingConversation(
  input: CreateMobileSchedulingConversationInput,
): Promise<Result<string>> {
  const { data, error } = await supabase.rpc('create_mobile_scheduling_conversation', {
    p_reference_type: input.referenceType,
    p_reference_id: input.referenceId,
    p_member_ids: input.memberIds,
    p_name: input.name ?? null,
  });
  if (error) return fail(error);
  return ok(data as string);
}

export interface ConversationAnchor {
  referenceType: string | null;
  referenceId: string | null;
}

/**
 * The scheduling row a mobile thread is anchored to.
 *
 * Read straight off `conversations` rather than through
 * list_my_mobile_conversations, which does not return the reference columns —
 * this keeps the in-thread decision bar shippable over OTA with no migration.
 * Safe because `conversations_select_member` (20260601000001_messaging_core.sql)
 * already scopes SELECT to members of the conversation.
 */
export async function getConversationAnchor(
  conversationId: string,
): Promise<Result<ConversationAnchor | null>> {
  const { data, error } = await supabase
    .from('conversations')
    .select('reference_type, reference_id')
    .eq('id', conversationId)
    .maybeSingle();
  if (error) return fail(error);
  if (!data) return ok(null);
  return ok({
    referenceType: (data as { reference_type: string | null }).reference_type ?? null,
    referenceId: (data as { reference_id: string | null }).reference_id ?? null,
  });
}
